// src/pages/messages-ai/ComposerWizard.jsx
//
// Dual-mode AI message composer.
//   • Single-team mode  (URL: /teams/:teamId/messages/new) — original flow.
//     Coach drafts a message for one team's roster. Backend uses
//     sendTeamBroadcast (per-team rate limit, per-team audit doc).
//   • Club mode         (URL: /clubs/:clubId/messages/new) — added May-2026.
//     Director drafts ONE message that fans out to multiple teams in the
//     club. Adds a new "Teams" step at the front of the wizard. Backend
//     uses sendClubBroadcastSms (club rate limit, per-team audit docs +
//     a club-level audit doc). Pre-selecting teams via ?teams=t1,t2,t3
//     is supported so the Programs bulk-send action bar can scope the
//     send to just the program's divisions.
//
// Mode is decided from useParams(): clubId set → club mode, teamId set →
// single-team. The two flows share Steps 1-3 (Type, Details, Review,
// Share) — only the audience-picker UI and the send/preview wiring fork.
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, updateDoc, serverTimestamp, addDoc,
  collection, query, where, orderBy, getDocs,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon, ChevronRightIcon, ArrowPathIcon,
  ClipboardDocumentIcon, CheckIcon, ShareIcon,
  PaperAirplaneIcon, EnvelopeIcon, DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  UsersIcon, AdjustmentsHorizontalIcon, TrophyIcon,
} from '@heroicons/react/24/outline';
import { MESSAGE_TYPES, TYPE_COLORS } from './messageTypes';
import EventPickerInline from '@/components/messages/EventPickerInline';
import { deriveDetailsForType } from '@/lib/messagePrefill';
import TeamSubNav from '@/components/team/TeamSubNav';
import useClub from '@/hooks/useClub';
// Voice chip on Review step — surfaces the magic. Pre-fix the user's
// chosen tone (set once in profile) was invoked silently by the backend
// and the marketing claim "drafts in your voice" had zero in-product
// signal. Now the coach can see which voice generated this message and
// jump to settings if it's wrong before sharing.
import { getMessageTone, DEFAULT_MESSAGE_TONE_ID } from '@/constants/messageTones';

/* ------------------------------------------------------------------ */
/* Bilingual UI strings                                                 */
/* ------------------------------------------------------------------ */
const T = {
  en: {
    // Single-team mode shows steps 1-4. Club mode prepends "Teams".
    steps: ['Type', 'Details', 'Review', 'Share'],
    stepsClub: ['Teams', 'Type', 'Details', 'Review', 'Share'],
    // Club-mode picker copy.
    clubTeamsTitle: 'Which teams should hear this?',
    clubTeamsSubtitle: 'Pick the teams that should receive this message. Default is all teams in the club.',
    clubAllTeams: 'All teams ({n})',
    clubTeamCount: '{n} team{s} selected',
    clubMultiTeamSubtitle: 'Both versions ready for your {n} team{s}.',
    clubReviewSubtitle: 'Both versions are ready. Edit either one before sharing.',
    clubProgramAudience: 'Program: {name}',
    clubProgramAudienceHint: 'Audience scoped to families registered for this program.',
    clubNoTeams: 'This club has no teams yet. Attach or create one first.',
    pickTitle: 'What type of message?',
    pickSubtitle: 'Choose the message category to get started.',
    selectDots: 'Select…',
    prefillBanner: 'Pre-filled from your last',
    prefillBannerSuffix: '— just update what changed.',
    clearAll: 'Clear all',
    coachNameLabel: 'Your name (for sign-off)',
    coachNamePlaceholder: 'Coach Maria',
    reviewTitle: 'Review your messages',
    reviewSubtitle: 'Both versions are ready. Edit either one before sharing.',
    english: 'English',
    spanish: 'Español',
    copy: 'Copy',
    copied: 'Copied!',
    copiedToast: 'copied!',
    shareTitle: 'Send to your families',
    shareSubtitle: 'One tap to reach your registered families. SMS for parents who opted in, email for the rest.',
    englishVersion: 'English version',
    spanishVersion: 'Versión en Español',
    copyEnglish: 'Copy English',
    copySpanish: 'Copy Español',
    copyBoth: 'Copy both versions',
    whatsappExplainBold: 'WhatsApp:',
    whatsappExplain: "Opens WhatsApp with the message pre-typed. Pick your family group and hit Send.",
    smsExplainBold: 'SMS:',
    smsExplain: "Opens your phone's Messages app with the message pre-typed. Works on iPhone and Android. Pick your contact or group and send.",
    saving: 'Saving…',
    markSent: '✓ I shared it — save to history',
    markSentHint: "Tap this after you've shared the message with families. It records it in your message history.",
    savedToast: 'Message saved to history!',
    saveErrToast: 'Could not save message.',
    genErrToast: 'Message generation failed. Please try again.',
    backToMessages: 'Back to Messages',
    back: 'Back',
    generating: 'Generating…',
    generateBtn: 'Generate messages',
    next: 'Next',
    // Send-to-roster (Phase 1 of the comms integration)
    sendToRoster: 'Send to families',
    sending: 'Sending…',
    audienceLoading: 'Loading audience…',
    audienceVia: 'via',
    audienceSms: 'SMS',
    audienceEmail: 'email',
    audienceUnreachable: 'unreachable',
    audienceEmpty: "No registered families to send to yet. Once parents register, or you invite them from the roster, they show up here. You can still copy the message and share it manually below.",
    rateLimitWarn: 'This team has sent {n} broadcasts in the last 24h. Limit is 5.',
    rateLimitHit: "You've hit today's broadcast limit for this team. Try again tomorrow.",
    confirmSendTitle: 'Send this message?',
    confirmSendBody: 'Going to {sms} families by SMS and {email} by email. This is final — messages go out immediately.',
    confirmSendGo: 'Yes, send now',
    confirmSendCancel: 'Cancel',
    sendReceipt: 'Sent: {sms} SMS · {email} email',
    sendReceiptPartial: 'Sent with issues: {sent} delivered · {failed} failed',
    sendReceiptHeld: '{held} SMS held by quiet hours (no texts 10 PM to 7 AM) — resend after 7 AM to reach them.',
    sendErr: 'Could not send. Please try again.',
    orShareManually: 'Or share manually with copy/paste',
    whatsappExplainNew: "Opens WhatsApp with the message pre-typed. Pick your family group and hit Send.",
    smsExplainNew: "Opens your phone's Messages app with the message pre-typed. Works on iPhone and Android.",
    // Audience filters
    audienceHeading: 'Audience',
    chooseAudience: 'Choose audience',
    audienceAllRoster: 'Everyone on the roster',
    audienceFilteredFrom: '{filtered} of {total} families match',
    filterSeason: 'Season',
    filterSeasonAll: 'All seasons',
    filterPayment: 'Payment',
    filterPaymentAll: 'All',
    filterPaymentPaid: 'Paid only',
    filterPaymentUnpaid: 'Unpaid only',
    filterLanguage: 'Language',
    filterLanguageAll: 'All',
    filterLanguageEn: 'English only',
    filterLanguageEs: 'Spanish only',
    filterReset: 'Reset filters',
    filterAudience: 'Audience',
    filterAudienceAll: 'Everyone',
    filterAudienceMissingDocs: 'Missing documents',
    // Single-recipient lock (set when ?prefilledFor is on the URL).
    prefilledLockBanner: 'Sending to: {player} ({parent})',
    prefilledLockUnlock: 'Or send to the whole team instead',
    // Roster Broadcast Bridge — shown only when the server preview reports
    // first-time roster emails in the audience.
    bdFirstContact: '{n} first-time email',
    bdNeedInvite: '{n} need a text invite',
    // Dark-bridge warning: roster contacts exist but broadcasts can't reach
    // them until they register (or the bridge goes live). Prevents the
    // "typed a roster, sent to nobody" silent failure.
    bdExcludedDark: "{n} roster contact(s) won't receive this yet. Parents get messages once they register. Share your season's registration link with them.",
    attestationLabel: 'These are my families and I have permission to contact them.',
    attestationErr: 'Please confirm you have permission to contact these families before sending.',
    // Invite nudge (roster-broadcast-bridge AC6) — makes the "invite them
    // from the roster" line in audienceEmpty actionable. Works with the
    // bridge dark (empty audience) and lit (phone-only contacts).
    inviteFamiliesBtn: 'Invite families from the roster',
  },
  es: {
    steps: ['Tipo', 'Detalles', 'Revisar', 'Compartir'],
    stepsClub: ['Equipos', 'Tipo', 'Detalles', 'Revisar', 'Compartir'],
    clubTeamsTitle: '¿Qué equipos deben recibirlo?',
    clubTeamsSubtitle: 'Elige los equipos que recibirán este mensaje. Por defecto son todos los equipos del club.',
    clubAllTeams: 'Todos los equipos ({n})',
    clubTeamCount: '{n} equipo{s} seleccionado{s}',
    clubMultiTeamSubtitle: 'Ambas versiones listas para tus {n} equipo{s}.',
    clubReviewSubtitle: 'Ambas versiones están listas. Edita cualquiera antes de compartir.',
    clubProgramAudience: 'Programa: {name}',
    clubProgramAudienceHint: 'Audiencia limitada a familias inscritas en este programa.',
    clubNoTeams: 'Este club aún no tiene equipos. Crea o adjunta uno primero.',
    pickTitle: '¿Qué tipo de mensaje?',
    pickSubtitle: 'Elige la categoría del mensaje para empezar.',
    selectDots: 'Selecciona…',
    prefillBanner: 'Pre-completado desde tu último',
    prefillBannerSuffix: '— solo actualiza lo que cambió.',
    clearAll: 'Borrar todo',
    coachNameLabel: 'Tu nombre (para la firma)',
    coachNamePlaceholder: 'Entrenador María',
    reviewTitle: 'Revisa tus mensajes',
    reviewSubtitle: 'Ambas versiones están listas. Edita cualquiera antes de compartir.',
    english: 'English',
    spanish: 'Español',
    copy: 'Copiar',
    copied: '¡Copiado!',
    copiedToast: 'copiado!',
    shareTitle: 'Envía a tus familias',
    shareSubtitle: 'Un toque para llegar a tus familias inscritas. SMS para los padres que aceptaron, correo para los demás.',
    englishVersion: 'Versión en Inglés',
    spanishVersion: 'Versión en Español',
    copyEnglish: 'Copiar Inglés',
    copySpanish: 'Copiar Español',
    copyBoth: 'Copiar ambas versiones',
    whatsappExplainBold: 'WhatsApp:',
    whatsappExplain: 'Abre WhatsApp con el mensaje ya escrito. Elige tu grupo de familias y pulsa Enviar.',
    smsExplainBold: 'SMS:',
    smsExplain: 'Abre la app de Mensajes de tu teléfono con el mensaje ya escrito. Funciona en iPhone y Android. Elige tu contacto o grupo y envía.',
    saving: 'Guardando…',
    markSent: '✓ Ya lo compartí — guardar en historial',
    markSentHint: 'Toca aquí después de compartir el mensaje con las familias. Lo guarda en tu historial de mensajes.',
    savedToast: '¡Mensaje guardado en el historial!',
    saveErrToast: 'No se pudo guardar el mensaje.',
    genErrToast: 'Falló la generación del mensaje. Por favor intenta de nuevo.',
    backToMessages: 'Volver a Mensajes',
    back: 'Atrás',
    generating: 'Generando…',
    generateBtn: 'Generar mensajes',
    next: 'Siguiente',
    // Enviar al equipo
    sendToRoster: 'Enviar a las familias',
    sending: 'Enviando…',
    audienceLoading: 'Cargando audiencia…',
    audienceVia: 'por',
    audienceSms: 'SMS',
    audienceEmail: 'correo',
    audienceUnreachable: 'sin contacto',
    audienceEmpty: 'Aún no hay familias inscritas para enviar. Cuando los padres se inscriban, o los invites desde el equipo, aparecerán aquí. Mientras tanto, puedes copiar el mensaje y compartirlo manualmente abajo.',
    rateLimitWarn: 'Este equipo ha enviado {n} mensajes en 24h. Límite: 5.',
    rateLimitHit: 'Has alcanzado el límite diario de mensajes para este equipo. Intenta mañana.',
    confirmSendTitle: '¿Enviar este mensaje?',
    confirmSendBody: 'Se enviará a {sms} familias por SMS y {email} por correo. Esta acción es final — los mensajes salen de inmediato.',
    confirmSendGo: 'Sí, enviar ahora',
    confirmSendCancel: 'Cancelar',
    sendReceipt: 'Enviado: {sms} SMS · {email} correo',
    sendReceiptPartial: 'Enviado con problemas: {sent} entregados · {failed} fallaron',
    sendReceiptHeld: '{held} SMS en espera por horario de descanso (no se envían textos de 10 PM a 7 AM) — reenvía después de las 7 AM.',
    sendErr: 'No se pudo enviar. Por favor intenta de nuevo.',
    orShareManually: 'O comparte manualmente copiando/pegando',
    whatsappExplainNew: 'Abre WhatsApp con el mensaje ya escrito. Elige tu grupo y pulsa Enviar.',
    smsExplainNew: 'Abre la app de Mensajes de tu teléfono con el mensaje ya escrito. Funciona en iPhone y Android.',
    // Filtros de audiencia
    audienceHeading: 'Audiencia',
    chooseAudience: 'Elegir audiencia',
    audienceAllRoster: 'Todo el equipo',
    audienceFilteredFrom: '{filtered} de {total} familias',
    filterSeason: 'Temporada',
    filterSeasonAll: 'Todas las temporadas',
    filterPayment: 'Pago',
    filterPaymentAll: 'Todos',
    filterPaymentPaid: 'Solo pagados',
    filterPaymentUnpaid: 'Solo pendientes',
    filterLanguage: 'Idioma',
    filterLanguageAll: 'Todos',
    filterLanguageEn: 'Solo Inglés',
    filterLanguageEs: 'Solo Español',
    filterReset: 'Restablecer filtros',
    filterAudience: 'Audiencia',
    filterAudienceAll: 'Todos',
    filterAudienceMissingDocs: 'Documentos faltantes',
    // Bloqueo de un solo destinatario (cuando ?prefilledFor está en la URL).
    prefilledLockBanner: 'Enviando a: {player} ({parent})',
    prefilledLockUnlock: 'O envía al equipo completo',
    // Puente de difusión al equipo — se muestra solo cuando la vista previa
    // del servidor reporta correos a familias por primera vez.
    bdFirstContact: '{n} correo por primera vez',
    bdNeedInvite: '{n} necesitan invitación por SMS',
    // Aviso del puente apagado: hay contactos en el equipo que los avisos
    // aún no pueden alcanzar hasta que se registren.
    bdExcludedDark: '{n} contacto(s) del equipo aún no recibirán esto. Los padres reciben mensajes cuando se registran. Compárteles el enlace de registro de tu temporada.',
    attestationLabel: 'Son mis familias y tengo permiso para contactarlas.',
    attestationErr: 'Confirma que tienes permiso para contactar a estas familias antes de enviar.',
    // Nudge de invitación (roster-broadcast-bridge AC6). "El equipo" matches
    // the audienceEmpty wording above.
    inviteFamiliesBtn: 'Invita a las familias desde el equipo',
  },
};

// ── localStorage helpers ──────────────────────────────────────────────────────

const LS_KEY = (typeId) => `scrimly.lastDetails.${typeId}`;

function saveLastDetails(typeId, details) {
  try {
    localStorage.setItem(LS_KEY(typeId), JSON.stringify(details));
  } catch {}
}

function loadLastDetails(typeId) {
  try {
    const raw = localStorage.getItem(LS_KEY(typeId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/* ---- Progress bar --------------------------------------------------------- */
function StepBar({ current, labels }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {labels.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <div className={[
              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors',
              i < current ? 'bg-[#b5f03d] text-[#0d1f14]'
              : i === current ? 'bg-[#b5f03d] text-[#0d1f14] ring-4 ring-[#b5f03d]/30'
              : 'bg-zinc-200 text-zinc-500',
            ].join(' ')}>
              {i < current ? <CheckIcon className="w-4 h-4" /> : i + 1}
            </div>
            <span className={[
              'text-sm font-medium hidden sm:block',
              i === current ? 'text-zinc-900 dark:text-white' : 'text-zinc-400',
            ].join(' ')}>
              {label}
            </span>
          </div>
          {i < labels.length - 1 && (
            <div className={[
              'flex-1 h-0.5 transition-colors',
              i < current ? 'bg-[#b5f03d]' : 'bg-zinc-200',
            ].join(' ')} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ---- Club Step 0: Pick teams ---------------------------------------------- */
// Only mounted in club mode (the URL is /clubs/:clubId/messages/new). The
// wizard always pre-selects every team in the club; the director can prune
// before advancing. When the audience was pre-scoped via ?teams= or by the
// Programs bulk-send action bar, those teamIds are pre-applied here so the
// director sees exactly the divisions the program spans.
function StepPickTeams({
  teams, teamsLoading, selectedTeamIds, onToggleTeam, onToggleAll,
  programName, tr,
}) {
  const allSelected = teams.length > 0 && selectedTeamIds.length === teams.length;
  return (
    <div>
      <h2 className="text-xl font-bold text-[#0d1f14] mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        {tr.clubTeamsTitle}
      </h2>
      <p className="text-sm text-zinc-500 mb-4">{tr.clubTeamsSubtitle}</p>

      {/* Program-scope hint — surfaces ?programId= when present so the
          director knows the audience is narrower than "every parent on
          these teams". The actual filter is enforced server-side. */}
      {programName && (
        <div className="mb-4 rounded-xl bg-[#b5f03d]/10 border border-[#b5f03d]/30 px-4 py-3">
          <p className="text-xs font-bold text-[#5a8a1f]" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' }}>
            {tr.clubProgramAudience.replace('{name}', programName)}
          </p>
          <p className="text-[11px] text-[#3d6010] mt-0.5">{tr.clubProgramAudienceHint}</p>
        </div>
      )}

      {teamsLoading ? (
        <div className="text-sm text-zinc-400 py-4 text-center">Loading teams…</div>
      ) : teams.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 p-8 text-center bg-white">
          <TrophyIcon className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">{tr.clubNoTeams}</p>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={onToggleAll}
            className={[
              'w-full flex items-center gap-3 rounded-2xl border-2 p-4 transition-all text-left mb-2',
              allSelected
                ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40',
            ].join(' ')}
          >
            <div className={[
              'h-5 w-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors',
              allSelected ? 'bg-[#b5f03d] border-[#b5f03d]' : 'border-zinc-400',
            ].join(' ')}>
              {allSelected && <CheckIcon className="h-3.5 w-3.5 text-[#0d1f14]" />}
            </div>
            <div>
              <p className="text-sm font-bold text-[#0d1f14]">
                {tr.clubAllTeams.replace('{n}', teams.length)}
              </p>
            </div>
          </button>

          <div className="space-y-2">
            {teams.map((team) => {
              const checked = selectedTeamIds.includes(team.id);
              return (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => onToggleTeam(team.id)}
                  className={[
                    'w-full flex items-center gap-3 rounded-2xl border-2 p-4 transition-all text-left',
                    checked
                      ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                      : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40',
                  ].join(' ')}
                >
                  <div className={[
                    'h-5 w-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors',
                    checked ? 'bg-[#b5f03d] border-[#b5f03d]' : 'border-zinc-400',
                  ].join(' ')}>
                    {checked && <CheckIcon className="h-3.5 w-3.5 text-[#0d1f14]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0d1f14]">{team.name}</p>
                    <p className="text-xs text-zinc-500">
                      {[team.sport, team.ageGroup, team.gender].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ---- Step 1: Pick type ---------------------------------------------------- */
function StepPickType({ selected, onSelect, tr, lang }) {
  return (
    <div>
      <h2 className="text-xl font-bold text-[#0d1f14] mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{tr.pickTitle}</h2>
      <p className="text-sm text-zinc-500 mb-6">{tr.pickSubtitle}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {MESSAGE_TYPES.map((type) => {
          const colors = TYPE_COLORS[type.color];
          const isSelected = selected?.id === type.id;
          const label = lang === 'es' ? (type.labelEs || type.label) : type.label;
          const description = lang === 'es' ? (type.descriptionEs || type.description) : type.description;
          return (
            <button
              key={type.id}
              type="button"
              onClick={() => onSelect(type)}
              className={[
                'flex flex-col items-center gap-2 rounded-2xl border-2 p-4 text-center transition-all hover:scale-[1.02] active:scale-[0.98]',
                isSelected
                  ? `${colors.border} ${colors.bg} shadow-md`
                  : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 hover:border-zinc-300',
              ].join(' ')}
            >
              <span className={`text-2xl flex items-center justify-center w-12 h-12 rounded-xl ${colors.icon}`}>
                {type.icon}
              </span>
              <span className="text-xs font-semibold text-zinc-800 dark:text-zinc-200 leading-tight">{label}</span>
              <span className="text-[11px] text-zinc-400 leading-tight hidden sm:block">{description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---- Step 2: Fill details ------------------------------------------------- */
function StepDetails({
  type, details, onChange, lastDetails, tr, lang,
  // Phase 2 picker wiring. teamIds is always the source of truth (multi-
  // team in club mode, single-element in single-team mode) so the inline
  // event picker pulls candidates from every selected team's game collection.
  teamIds,
  selectedEventId, onPickEvent, onSkipPick,
  autoFilled,
}) {
  const colors = TYPE_COLORS[type.color];
  const hasLastDetails = lastDetails && Object.keys(lastDetails).length > 0;
  const typeLabel = lang === 'es' ? (type.labelEs || type.label) : type.label;
  const typeDescription = lang === 'es' ? (type.descriptionEs || type.description) : type.description;

  const renderField = (field) => {
    const value = details[field.name] || '';
    // Highlight fields whose value still matches what we auto-filled —
    // mirrors the slide-over pattern (lime border + ✨ badge).
    const isAutoFilled = autoFilled && autoFilled[field.name] != null
      && String(autoFilled[field.name]).length > 0
      && String(value) === String(autoFilled[field.name]);
    const borderClass = isAutoFilled
      ? 'border-[#b5f03d]/50 bg-[#b5f03d]/5 dark:bg-[#b5f03d]/10'
      : 'border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800';
    const baseClass = `mt-1 w-full rounded-xl border ${borderClass} px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/50 focus:border-[#b5f03d] transition-colors`;
    const placeholder = lang === 'es' ? (field.placeholderEs || field.placeholder) : field.placeholder;

    if (field.type === 'select') {
      return (
        <select value={value} onChange={(e) => onChange(field.name, e.target.value)} className={baseClass} required={field.required}>
          <option value="">{tr.selectDots}</option>
          {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    if (field.type === 'textarea') {
      return <textarea value={value} onChange={(e) => onChange(field.name, e.target.value)} className={baseClass + ' resize-none'} rows={3} placeholder={placeholder} required={field.required} />;
    }
    return <input type={field.type} value={value} onChange={(e) => onChange(field.name, e.target.value)} className={baseClass} placeholder={placeholder} required={field.required} />;
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className={`text-2xl flex items-center justify-center w-12 h-12 rounded-xl ${colors.icon} flex-shrink-0`}>
          {type.icon}
        </span>
        <div>
          <h2 className="text-xl font-bold text-[#0d1f14]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{typeLabel}</h2>
          <p className="text-sm text-zinc-500">{typeDescription}</p>
        </div>
      </div>

      {/* Phase 2 — pre-fill from a real event the coach has on the schedule.
          The picker is a no-op for message types where event prefill makes
          no sense (Payment Due, Motivation, etc.) — it just renders a
          short hint or nothing. */}
      {teamIds && teamIds.length > 0 && (
        <EventPickerInline
          teamIds={teamIds}
          messageType={type.id}
          selectedId={selectedEventId}
          onSelect={(eid, candidate) => onPickEvent(eid, candidate)}
          onSkip={onSkipPick}
        />
      )}

      {/* Pre-fill from last message banner */}
      {hasLastDetails && (
        <div className="mb-4 rounded-xl bg-[#b5f03d]/10 border border-[#b5f03d]/30 px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-xs text-[#5a8a1f] font-medium">
            ↩ {tr.prefillBanner} {typeLabel} {tr.prefillBannerSuffix}
          </p>
          <button
            type="button"
            onClick={() => {
              type.fields.forEach((f) => onChange(f.name, ''));
              onChange('coachName', '');
            }}
            className="text-xs text-[#5a8a1f] hover:text-[#3d6010] underline underline-offset-2 whitespace-nowrap"
          >
            {tr.clearAll}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {type.fields.map((field) => {
          const fieldLabel = lang === 'es' ? (field.labelEs || field.label) : field.label;
          const value = details[field.name] || '';
          const isAutoFilled = autoFilled && autoFilled[field.name] != null
            && String(autoFilled[field.name]).length > 0
            && String(value) === String(autoFilled[field.name]);
          return (
            <div key={field.name} className={field.half ? 'inline-block w-[calc(50%-0.5rem)] mr-4 last:mr-0 align-top' : ''}>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-zinc-500 dark:text-zinc-400">
                  {fieldLabel}
                  {field.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                {isAutoFilled && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-[#5a8a1f]"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' }}
                    title="Auto-filled from the picked event"
                  >
                    ✨ Auto-filled
                  </span>
                )}
              </div>
              {renderField(field)}
            </div>
          );
        })}
      </div>

      {/* Coach name */}
      <div className="mt-4 pt-4 border-t border-zinc-100 dark:border-zinc-800">
        <label className="block text-xs font-semibold text-zinc-500 mb-1">
          {tr.coachNameLabel}
        </label>
        <input
          type="text"
          value={details.coachName || ''}
          onChange={(e) => onChange('coachName', e.target.value)}
          className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/50 focus:border-[#b5f03d] transition-colors"
          placeholder={tr.coachNamePlaceholder}
        />
      </div>
    </div>
  );
}

/* ---- Copy button ---------------------------------------------------------- */
function CopyButton({ text, label, className, copiedLabel, copiedToast }) {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} ${copiedToast}`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={handle}
      className={[
        'inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors',
        copied ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : className,
      ].join(' ')}
    >
      {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
      {copied ? copiedLabel : label}
    </button>
  );
}

/* ---- Voice chip ------------------------------------------------------------ */
// Tiny lime pill on Review that names the tone the AI just wrote in.
// Reads from userProfile.messageTone (the same field generateMessage
// reads server-side) — keeping the surface and the source-of-truth
// aligned. If unset, falls back to "warm" which mirrors
// DEFAULT_MESSAGE_TONE_ID + the backend's default in core/messageTones.
function VoiceChip({ messageTone, onChange, lang }) {
  const tone = getMessageTone(messageTone);
  const isDefault = !messageTone || messageTone === DEFAULT_MESSAGE_TONE_ID;
  // Match the user's example: "Voice: Warm coach (default)" when unset,
  // otherwise "Voice: <Tone Label>". Picker labels are English-only
  // today; if/when localized, swap in tone.labelEs here.
  const label = isDefault && !messageTone
    ? (lang === 'es' ? 'Entrenador cálido (predeterminado)' : 'Warm coach (default)')
    : tone.label;
  const changeLabel = lang === 'es' ? 'cambiar para este mensaje' : 'change for this message';
  const voiceLabel  = lang === 'es' ? 'Voz' : 'Voice';
  return (
    <div className="mb-6 flex flex-wrap items-center gap-2">
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 px-3 py-1 text-xs font-semibold text-[#0d1f14]"
        title={tone.description}
      >
        <span aria-hidden="true">🎙️</span>
        {voiceLabel}: <span className="font-bold">{label}</span>
      </span>
      <button
        type="button"
        onClick={onChange}
        className="text-xs text-[#5a8a1f] hover:text-[#3d6010] underline underline-offset-2"
      >
        {changeLabel}
      </button>
    </div>
  );
}

/* ---- Step 3: Review ------------------------------------------------------- */
function StepReview({ type, bodyEn, bodyEs, onEditEn, onEditEs, tr, messageTone, onChangeVoice, lang, teamCount }) {
  // Subtitle includes the team count when in club mode (teamCount > 1) so the
  // director sees "Both versions ready for your 3 teams" — otherwise the
  // single-team default copy.
  const subtitle = teamCount && teamCount > 1
    ? tr.clubMultiTeamSubtitle.replace('{n}', teamCount).replace('{s}', teamCount === 1 ? '' : 's')
    : tr.reviewSubtitle;
  return (
    <div>
      <h2 className="text-xl font-bold text-[#0d1f14] mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{tr.reviewTitle}</h2>
      <p className="text-sm text-zinc-500 mb-3">
        {subtitle}
      </p>
      {/* Voice chip — names the tone that drafted these messages. */}
      <VoiceChip messageTone={messageTone} onChange={onChangeVoice} lang={lang} />
      <div className="space-y-6">
        <div className="rounded-2xl border border-blue-200 dark:border-blue-800 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2">
              <span className="text-base">🇺🇸</span>
              <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">{tr.english}</span>
            </div>
            <CopyButton text={bodyEn} label={tr.copy} copiedLabel={tr.copied} copiedToast={tr.copiedToast} className="border border-blue-300 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 bg-white dark:bg-transparent text-xs py-1 px-3" />
          </div>
          <textarea value={bodyEn} onChange={(e) => onEditEn(e.target.value)} className="w-full px-4 py-3 text-sm text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 resize-none focus:outline-none" rows={6} />
        </div>
        <div className="rounded-2xl border border-amber-200 dark:border-amber-800 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2">
              <span className="text-base">🇲🇽</span>
              <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">{tr.spanish}</span>
            </div>
            <CopyButton text={bodyEs} label={tr.copy} copiedLabel={tr.copied} copiedToast={tr.copiedToast} className="border border-amber-300 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 bg-white dark:bg-transparent text-xs py-1 px-3" />
          </div>
          <textarea value={bodyEs} onChange={(e) => onEditEs(e.target.value)} className="w-full px-4 py-3 text-sm text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 resize-none focus:outline-none" rows={6} />
        </div>
      </div>
    </div>
  );
}

/* ---- Reusable share button icons ------------------------------------------ */
const WhatsAppIcon = () => (
  <svg className="h-4 w-4 fill-white flex-shrink-0" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
  </svg>
);

const SmsIcon = () => (
  <svg className="h-4 w-4 fill-white flex-shrink-0" viewBox="0 0 24 24">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
    <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
  </svg>
);

/* ---- ShareButtons: WhatsApp + SMS side by side ---------------------------- */
function ShareButtons({ text, copyLabel, copyClassName, copiedLabel, copiedToast }) {
  const waUrl  = `https://wa.me/?text=${encodeURIComponent(text)}`;
  const smsUrl = `sms:?body=${encodeURIComponent(text)}`;
  return (
    <div className="flex gap-2 flex-wrap">
      <CopyButton text={text} label={copyLabel} className={copyClassName} copiedLabel={copiedLabel} copiedToast={copiedToast} />
      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1ebe57] transition-colors"
      >
        <WhatsAppIcon /> WhatsApp
      </a>
      <a
        href={smsUrl}
        className="inline-flex items-center gap-2 rounded-xl bg-blue-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-400 transition-colors"
      >
        <SmsIcon /> SMS
      </a>
    </div>
  );
}

/* ---- Audience summary pill ------------------------------------------------ */
// Shows "89 via SMS · 38 via email" once the preview callable resolves.
// Its whole job is to answer the coach's first question: "who actually
// gets this?" — before they tap Send. No counts = no send button.
function AudienceSummary({ audience, loading, tr }) {
  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
        <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
        {tr.audienceLoading}
      </div>
    );
  }
  if (!audience) return null;
  const {
    smsCount, emailCount, unreachableCount,
    // Roster Broadcast Bridge fields (0 when the flag is off, so this whole
    // second line stays hidden until go-live).
    firstContactEmailCount = 0, rosterPhoneOnlyCount = 0,
    // Dark-bridge counterpart: >0 only while the flag is OFF and the roster
    // has contacts a send would skip. Mutually exclusive with the two above.
    rosterContactsExcluded = 0,
  } = audience;
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-600 dark:text-zinc-300">
        <span className="inline-flex items-center gap-1">
          <DevicePhoneMobileIcon className="h-3.5 w-3.5 text-emerald-500" />
          <strong className="font-semibold text-zinc-900 dark:text-white">{smsCount}</strong>
          {tr.audienceVia} {tr.audienceSms}
        </span>
        <span className="inline-flex items-center gap-1">
          <EnvelopeIcon className="h-3.5 w-3.5 text-blue-500" />
          <strong className="font-semibold text-zinc-900 dark:text-white">{emailCount}</strong>
          {tr.audienceVia} {tr.audienceEmail}
        </span>
        {unreachableCount > 0 && (
          <span className="inline-flex items-center gap-1 text-zinc-400">
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            {unreachableCount} {tr.audienceUnreachable}
          </span>
        )}
      </div>
      {(firstContactEmailCount > 0 || rosterPhoneOnlyCount > 0) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500 dark:text-zinc-400">
          {firstContactEmailCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <EnvelopeIcon className="h-3 w-3 text-amber-500" />
              {tr.bdFirstContact.replace('{n}', firstContactEmailCount)}
            </span>
          )}
          {rosterPhoneOnlyCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <DevicePhoneMobileIcon className="h-3 w-3 text-zinc-400" />
              {tr.bdNeedInvite.replace('{n}', rosterPhoneOnlyCount)}
            </span>
          )}
        </div>
      )}
      {rosterContactsExcluded > 0 && (
        <div className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 text-[11px] leading-snug text-amber-700 dark:text-amber-400">
          <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{tr.bdExcludedDark.replace('{n}', rosterContactsExcluded)}</span>
        </div>
      )}
    </>
  );
}

/* ---- Confirm-send modal --------------------------------------------------- */
// Broadcasts are irreversible (texts hit phones instantly), so we make the
// coach confirm once. This is the only safety net between "tap wrong
// button" and "89 parents get a duplicate message at 10pm".
function ConfirmSendModal({ audience, onConfirm, onCancel, sending, tr }) {
  // Local, resets each time the modal opens. The server persists the
  // attestation across sessions, so a coach only ever sees this once.
  const [attested, setAttested] = useState(false);
  if (!audience) return null;
  // attestationRequired is set by the server preview only when there are
  // first-time roster emails in the audience AND this coach hasn't attested.
  const needsAttestation = !!audience.attestationRequired;
  const body = tr.confirmSendBody
    .replace('{sms}',   audience.smsCount)
    .replace('{email}', audience.emailCount);
  const blocked = sending || (needsAttestation && !attested);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-zinc-900 p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-zinc-900 dark:text-white">{tr.confirmSendTitle}</h3>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">{body}</p>
        {needsAttestation && (
          <label className="mt-4 flex items-start gap-2 cursor-pointer rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
            <input
              type="checkbox"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
              disabled={sending}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]"
            />
            <span className="text-xs text-amber-900 dark:text-amber-100">{tr.attestationLabel}</span>
          </label>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50 transition-colors"
          >
            {tr.confirmSendCancel}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(attested)}
            disabled={blocked}
            className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2 text-sm font-bold uppercase disabled:opacity-60 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' }}
          >
            {sending
              ? <><ArrowPathIcon className="h-4 w-4 animate-spin" />{tr.sending}</>
              : <><PaperAirplaneIcon className="h-4 w-4" />{tr.confirmSendGo}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Audience filter panel ------------------------------------------------ */
// Filter chips for payment + language, plus a season dropdown. Small,
// collapsible, and fires a callback on every change so the caller can
// re-run the preview. The panel itself is stateless — the ComposerWizard
// owns filter state so it survives the collapse/expand cycle.
function FilterChipGroup({ label, value, options, onChange }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-1.5">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={[
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                active
                  ? 'border-[#b5f03d] bg-[#b5f03d]/10 text-[#5a8a1f] font-semibold'
                  : 'border-zinc-200 text-zinc-600 hover:bg-[#f7faf5]',
              ].join(' ')}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AudienceFilters({
  filters, onChange, onReset,
  seasons, seasonsLoading,
  tr,
}) {
  const hasFilters =
    filters.seasonId ||
    filters.paymentStatus !== 'all' ||
    filters.language !== 'all' ||
    filters.audienceMode !== 'all';
  return (
    <div className="mt-4 space-y-3 border-t border-zinc-200 dark:border-zinc-700 pt-4">
      {/* Season selector — only shown once seasons are loaded to avoid
          an empty <select> flashing in. Unlike the chip groups, season is
          unbounded (one per registration cycle) so a dropdown scales. */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-1.5">
          {tr.filterSeason}
        </p>
        <select
          value={filters.seasonId || ''}
          onChange={(e) => onChange({ seasonId: e.target.value || null })}
          disabled={seasonsLoading}
          className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/50 focus:border-[#b5f03d] disabled:opacity-60"
        >
          <option value="">{tr.filterSeasonAll}</option>
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>{s.name || s.id}</option>
          ))}
        </select>
      </div>

      <FilterChipGroup
        label={tr.filterPayment}
        value={filters.paymentStatus}
        onChange={(v) => onChange({ paymentStatus: v })}
        options={[
          { value: 'all',    label: tr.filterPaymentAll },
          { value: 'paid',   label: tr.filterPaymentPaid },
          { value: 'unpaid', label: tr.filterPaymentUnpaid },
        ]}
      />

      <FilterChipGroup
        label={tr.filterLanguage}
        value={filters.language}
        onChange={(v) => onChange({ language: v })}
        options={[
          { value: 'all', label: tr.filterLanguageAll },
          { value: 'en',  label: tr.filterLanguageEn  },
          { value: 'es',  label: tr.filterLanguageEs  },
        ]}
      />

      {/* Audience mode — currently the only non-default option is "missing
          documents", which scopes to families with at least one
          required-doc gap. Backend needs the season to know which docs
          count, so the chip is disabled when no season is picked. */}
      <FilterChipGroup
        label={tr.filterAudience}
        value={filters.audienceMode}
        onChange={(v) => onChange({ audienceMode: v })}
        options={[
          { value: 'all',          label: tr.filterAudienceAll },
          { value: 'missing_docs', label: tr.filterAudienceMissingDocs },
        ]}
      />

      {hasFilters && (
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 underline underline-offset-2"
        >
          {tr.filterReset}
        </button>
      )}
    </div>
  );
}

/* ---- Routing summary ------------------------------------------------------ */
// Surfaces the magic — pre-fix the per-language routing happened silently
// and coaches had no idea the bilingual + auto-translate pipeline was
// firing on their behalf. Now the Share step opens with "🇲🇽 4 in Español
// · SMS to 4 (auto-translated)" so the value is loud.
//
// Counts come from the single `previewTeamBroadcast` response — its
// `languageBreakdown` field returns per-language × per-channel buckets
// computed in the same loop as the totals. (Pre-refactor we fired two
// extra preview calls to derive the same breakdown client-side.)
function RoutingSummary({ audience, langSplit, langSplitLoading, forceBilingual, lang }) {
  const heading = lang === 'es'
    ? 'Auto-enrutado por idioma + canal preferido'
    : 'Auto-routed by language + channel preference';
  const noRecipients = lang === 'es'
    ? 'Nadie coincide con tu filtro de audiencia.'
    : 'No recipients match your audience filter.';
  const inEnglish    = lang === 'es' ? 'en Inglés'   : 'in English';
  const inSpanish    = lang === 'es' ? 'en Español'  : 'in Español';
  const smsTo        = lang === 'es' ? 'SMS para'    : 'SMS to';
  const emailTo      = lang === 'es' ? 'correo para' : 'email to';
  const autoXlated   = lang === 'es' ? '(traducido automáticamente)' : '(auto-translated)';
  const emailOnly    = lang === 'es' ? 'solo por correo' : 'by email only';
  const noSpanishYet = lang === 'es'
    ? 'Versión en español generada — compártela manualmente con cualquier familia hispanohablante.'
    : 'Spanish version generated — share manually with any Spanish-speaking families.';

  // Empty audience after filtering — short-circuit before showing any
  // language rows that would all read "0".
  const totalRecipients = (audience?.smsCount || 0) + (audience?.emailCount || 0);
  if (totalRecipients === 0) {
    return (
      <div className="mb-4 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-[#f7faf5] dark:bg-zinc-800/40 p-4">
        <h3
          className="text-sm font-bold text-[#0d1f14] dark:text-white mb-1"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.3px' }}
        >
          {heading}
        </h3>
        <p className="text-xs text-zinc-500">{noRecipients}</p>
      </div>
    );
  }

  // Prefer the per-language split when available; fall back to the
  // "everyone" totals while the second/third preview calls resolve so
  // the block doesn't pop in late.
  const en = langSplit?.en || null;
  const es = langSplit?.es || null;

  const enTotal = en ? en.total      : null;
  const enSms   = en ? en.smsCount   : null;
  const enEmail = en ? en.emailCount : null;
  const esTotal = es ? es.total      : null;
  const esSms   = es ? es.smsCount   : null;
  const esEmail = es ? es.emailCount : null;

  // forceBilingual + zero Spanish parents = the AI generated a Spanish
  // version that nobody on the roster will receive via auto-route.
  // Surface a gentle nudge so the coach knows it's there for manual
  // share (community board, group chat, etc.).
  const showOrphanedSpanishNote =
    !!forceBilingual && es != null && esTotal === 0;

  return (
    <div className="mb-4 rounded-2xl border border-[#b5f03d]/30 bg-[#b5f03d]/5 p-4">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <h3
          className="text-sm font-bold text-[#0d1f14] dark:text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.3px' }}
        >
          {heading}
        </h3>
        {langSplitLoading && (
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-[#5a8a1f]" />
        )}
      </div>

      <ul className="space-y-1.5 text-xs text-zinc-700 dark:text-zinc-200">
        {/* English row — only render when there's actually anyone in this bucket. */}
        {(enTotal == null || enTotal > 0) && (
          <li className="flex flex-wrap items-baseline gap-x-1.5">
            <span aria-hidden="true">🇺🇸</span>
            <strong className="font-semibold text-zinc-900 dark:text-white">
              {enTotal != null ? enTotal : '—'} {inEnglish}
            </strong>
            {enTotal != null && enTotal > 0 && (
              <span className="text-zinc-500">
                · {smsTo} {enSms}, {emailTo} {enEmail}
              </span>
            )}
          </li>
        )}
        {/* Spanish row — same gate. */}
        {(esTotal == null || esTotal > 0) && (
          <li className="flex flex-wrap items-baseline gap-x-1.5">
            <span aria-hidden="true">🇲🇽</span>
            <strong className="font-semibold text-zinc-900 dark:text-white">
              {esTotal != null ? esTotal : '—'} {inSpanish}
            </strong>
            {esTotal != null && esTotal > 0 && (
              <span className="text-zinc-500">
                · {smsTo} {esSms} <em className="not-italic text-[#5a8a1f]">{autoXlated}</em>
              </span>
            )}
          </li>
        )}
        {/* Email-only line — distinct from the per-language breakdown
            because it's the channel-mix view ("how many never see SMS?"). */}
        {(audience?.emailCount || 0) > 0 && (
          <li className="flex flex-wrap items-baseline gap-x-1.5">
            <span aria-hidden="true">📧</span>
            <strong className="font-semibold text-zinc-900 dark:text-white">
              {audience.emailCount}
            </strong>
            <span className="text-zinc-500">{emailOnly}</span>
          </li>
        )}
      </ul>

      {showOrphanedSpanishNote && (
        <p className="mt-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200">
          {noSpanishYet}
        </p>
      )}
    </div>
  );
}

/* ---- Step 4: Share -------------------------------------------------------- */
// Two paths, in priority order:
//   1. **Send to roster** (primary) — fires the `sendTeamBroadcast` callable
//      and the backend fans out SMS + email to the team's actual parents.
//      This is the whole point of the comms integration — no copy/paste.
//   2. **Share manually** (secondary, collapsed below) — the old WhatsApp /
//      SMS / copy buttons. Kept for coaches who still prefer a group chat,
//      or for when the backend is down / rate-limited.
function StepShare({
  bodyEn, bodyEs, tr,
  audience, audienceLoading, audienceError,
  langSplit, langSplitLoading,
  onRequestSend, onMarkSent, sending, sentReceipt,
  filters, onFiltersChange, onFiltersReset,
  seasons, seasonsLoading,
  prefilledRegistration, onUnlockPrefilled,
  onInviteFamilies,   // null in club mode (invites are per-team)
  lang,
}) {
  const rateLimited = audience && audience.rateLimitRemaining === 0;
  const noAudience  = audience && audience.smsCount === 0 && audience.emailCount === 0;
  const canSend     = audience && !noAudience && !rateLimited && !sentReceipt;

  // Show a "47 of 89" note when filters actually narrow the roster. Skipping
  // this when post-filter equals pre-filter keeps the UI quiet in the
  // default-to-everyone case.
  const filteredNote = audience && audience.rosterTotal != null
    && audience.totalRegistrations !== audience.rosterTotal
    ? tr.audienceFilteredFrom
        .replace('{filtered}', audience.totalRegistrations)
        .replace('{total}',    audience.rosterTotal)
    : null;

  const [filtersOpen, setFiltersOpen] = useState(
    // Default collapsed, but auto-expand if the coach already has non-default
    // filters — happens on URL-seeded loads from FinancialReports /
    // RegistrationsDashboard (?audience=unpaid|missing_docs, ?seasonId=…)
    // so the coach can immediately see what got pre-filled.
    filters.seasonId != null
    || filters.paymentStatus !== 'all'
    || filters.language     !== 'all'
    || filters.audienceMode !== 'all'
  );

  return (
    <div>
      <h2 className="text-xl font-bold text-[#0d1f14] mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{tr.shareTitle}</h2>
      <p className="text-sm text-zinc-500 mb-4">
        {tr.shareSubtitle}
      </p>

      {/* Routing summary — surfaces the per-language auto-routing that
          used to happen silently. The bilingual + per-parent-language
          routing is genuine MAGIC; this block makes it loud. */}
      {audience && !audienceLoading && (
        <RoutingSummary
          audience={audience}
          langSplit={langSplit}
          langSplitLoading={langSplitLoading}
          forceBilingual={true}  // composer always generates both versions
          lang={lang}
        />
      )}

      {/* --- Primary: Send to roster ------------------------------------- */}
      <div className="rounded-2xl border-2 border-[#b5f03d]/40 bg-[#b5f03d]/10 p-5 mb-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <PaperAirplaneIcon className="h-5 w-5 text-[#5a8a1f] flex-shrink-0" />
              <h3 className="text-base font-bold text-zinc-900 dark:text-white truncate">
                {tr.sendToRoster}
              </h3>
            </div>
            <div className="mt-1.5">
              <AudienceSummary
                audience={audience}
                loading={audienceLoading}
                tr={tr}
              />
              {filteredNote && (
                <p className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
                  <UsersIcon className="inline-block h-3 w-3 mr-0.5 -mt-0.5" />
                  {filteredNote}
                </p>
              )}
            </div>
          </div>
          {/* "Choose audience" toggle — lives in the card, not the body,
              because filters are an adjustment *to the send*, not a
              separate concept. */}
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="flex-shrink-0 inline-flex items-center gap-1 rounded-lg border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-700 bg-white hover:bg-[#f7faf5] transition-colors"
            aria-expanded={filtersOpen}
          >
            <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
            {tr.chooseAudience}
          </button>
        </div>

        {/* Prefilled-single-recipient banner — set when the composer was
            opened from a per-row "Send reminder" action (FinancialReports).
            The audience is locked to one parent until the coach explicitly
            broadens with the unlock link. Renders above the filter panel
            so it's still visible when the panel is collapsed. */}
        {prefilledRegistration && (
          <div className="mt-3 mb-3 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-3 py-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <p className="text-xs text-[#0d1f14] dark:text-white">
              <span className="font-semibold">
                {tr.prefilledLockBanner
                  .replace('{player}', prefilledRegistration.playerName
                    || [prefilledRegistration.playerFirstName, prefilledRegistration.playerLastName].filter(Boolean).join(' ')
                    || '—')
                  .replace('{parent}', prefilledRegistration.parentName || '—')}
              </span>
            </p>
            <button
              type="button"
              onClick={onUnlockPrefilled}
              className="text-xs text-[#5a8a1f] hover:text-[#3d6010] underline underline-offset-2 whitespace-nowrap"
            >
              {tr.prefilledLockUnlock}
            </button>
          </div>
        )}

        {filtersOpen && (
          <AudienceFilters
            filters={filters}
            onChange={onFiltersChange}
            onReset={onFiltersReset}
            seasons={seasons}
            seasonsLoading={seasonsLoading}
            tr={tr}
          />
        )}

        {/* Empty roster / rate-limit warnings — shown inline, not as toasts,
            because they change the affordance (button disabled + reason). */}
        {audience && noAudience && (
          <p className="mb-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-xs px-3 py-2">
            {tr.audienceEmpty}
          </p>
        )}
        {/* Invite on-ramp: always available in single-team mode so a coach can
            reach the roster's invite flow ("X parents not yet invited → Invite
            all") straight from the send step, whether or not anyone is reachable
            yet. The roster page owns the actual invite UI + status badges; this
            is just the reliable, findable entry point. */}
        {onInviteFamilies && !sentReceipt && (
          <button
            type="button"
            onClick={onInviteFamilies}
            className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#5a8a1f] hover:bg-[#f7faf5] transition-colors"
          >
            <UsersIcon className="h-3.5 w-3.5" />
            {tr.inviteFamiliesBtn}
          </button>
        )}
        {rateLimited && (
          <p className="mb-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-xs px-3 py-2">
            {tr.rateLimitHit}
          </p>
        )}
        {audience && audience.rateLimitRemaining > 0 && audience.rateLimitRemaining <= 2 && (
          <p className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs px-3 py-2">
            {tr.rateLimitWarn.replace('{n}', 5 - audience.rateLimitRemaining)}
          </p>
        )}

        {/* Post-send receipt replaces the button once it's done, so the
            coach doesn't second-guess and re-tap. */}
        {sentReceipt ? (
          <div className="rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 px-4 py-3 text-sm font-semibold flex items-center gap-2">
            <CheckIcon className="h-5 w-5" />
            {sentReceipt}
          </div>
        ) : (
          <button
            type="button"
            onClick={onRequestSend}
            disabled={!canSend || sending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-3 text-sm font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' }}
          >
            {sending
              ? <><ArrowPathIcon className="h-4 w-4 animate-spin" />{tr.sending}</>
              : <><PaperAirplaneIcon className="h-4 w-4" />{tr.sendToRoster}</>}
          </button>
        )}
      </div>

      {/* --- Secondary: Copy / WhatsApp / SMS (manual share) -------------- */}
      <details className="group">
        <summary className="cursor-pointer list-none text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors select-none flex items-center gap-1">
          <ChevronRightIcon className="h-4 w-4 transition-transform group-open:rotate-90" />
          {tr.orShareManually}
        </summary>

        <div className="mt-4 space-y-4">
          {/* English */}
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5 bg-white dark:bg-zinc-800">
            <div className="flex items-center gap-2 mb-3">
              <span>🇺🇸</span>
              <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{tr.englishVersion}</span>
            </div>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4 leading-relaxed bg-zinc-50 dark:bg-zinc-900 rounded-xl p-3 whitespace-pre-line">
              {bodyEn}
            </p>
            <ShareButtons
              text={bodyEn}
              copyLabel={tr.copyEnglish}
              copiedLabel={tr.copied}
              copiedToast={tr.copiedToast}
              copyClassName="bg-blue-600 text-white hover:bg-blue-500"
            />
          </div>

          {/* Spanish */}
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5 bg-white dark:bg-zinc-800">
            <div className="flex items-center gap-2 mb-3">
              <span>🇲🇽</span>
              <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{tr.spanishVersion}</span>
            </div>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4 leading-relaxed bg-zinc-50 dark:bg-zinc-900 rounded-xl p-3 whitespace-pre-line">
              {bodyEs}
            </p>
            <ShareButtons
              text={bodyEs}
              copyLabel={tr.copySpanish}
              copiedLabel={tr.copied}
              copiedToast={tr.copiedToast}
              copyClassName="bg-amber-500 text-white hover:bg-amber-400"
            />
          </div>

          {/* Copy both */}
          <CopyButton
            text={`🇺🇸 English:\n${bodyEn}\n\n🇲🇽 Español:\n${bodyEs}`}
            label={tr.copyBoth}
            copiedLabel={tr.copied}
            copiedToast={tr.copiedToast}
            className="w-full justify-center border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
          />

          {/* Mark-sent button: lets coaches who *did* use copy/paste record
              the send in history. Only visible under the manual-share
              accordion so it doesn't compete with the primary send. */}
          <div className="pt-2">
            <button
              type="button"
              onClick={onMarkSent}
              disabled={sending || !!sentReceipt}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60 transition-colors"
            >
              {sending ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
              {sending ? tr.saving : tr.markSent}
            </button>
            <p className="mt-1.5 text-xs text-zinc-400">{tr.markSentHint}</p>
          </div>
        </div>
      </details>
    </div>
  );
}

/* ---- Main Component ------------------------------------------------------- */
export default function ComposerWizard() {
  // Mode detection: clubId set → club (multi-team) mode; teamId set →
  // single-team mode (legacy default). Both are read off useParams() so
  // App.jsx wires them through different route paths.
  const { teamId, clubId } = useParams();
  const isClubMode = !!clubId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile, firebaseUser } = useAuth();
  const { i18n } = useTranslation();
  const lang = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const tr = T[lang];

  // Club teams (only loaded in club mode). useClub gives us the doc plus
  // the loading flag — we still fan out per-team Firestore reads to grab
  // each team's name/sport/age (the club doc just stores teamIds).
  const { club, loading: clubLoading } = useClub(isClubMode ? clubId : null);
  const [clubTeams, setClubTeams] = useState([]);
  const [clubTeamsLoading, setClubTeamsLoading] = useState(isClubMode);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [didApplyTeamsParam, setDidApplyTeamsParam] = useState(false);

  // Program-scope (only meaningful in club mode + when the URL carries
  // ?audience=program-registrants&programId=…). Loaded lazily so the
  // program name can show up in the team-picker hint even when programId
  // came in without an explicit ?teams= list.
  const [program, setProgram] = useState(null);

  // Step is 0..3 in single-team mode, 0..4 in club mode (extra "Teams"
  // step at index 0). The substantive step semantics are identical
  // beyond the offset; helpers below convert between the two so the
  // rest of the component still thinks in terms of substantive steps.
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [details, setDetails] = useState({});
  const [lastDetails, setLastDetails] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [messageId, setMessageId] = useState(null);
  const [clubBroadcastId, setClubBroadcastId] = useState(null);   // club-mode draft audit doc id
  const [bodyEn, setBodyEn] = useState('');
  const [bodyEs, setBodyEs] = useState('');
  const [sending, setSending] = useState(false);

  // Substantive-step indexing helpers. Single-team substantive steps are
  // PICK_TYPE=0, DETAILS=1, REVIEW=2, SHARE=3. In club mode they shift up
  // by 1 because TEAMS=0 is prepended. Existing code reasons in
  // substantive terms — these helpers translate.
  const TEAMS_STEP = isClubMode ? 0 : -1;
  const PICK_TYPE_STEP = isClubMode ? 1 : 0;
  const DETAILS_STEP   = isClubMode ? 2 : 1;
  const REVIEW_STEP    = isClubMode ? 3 : 2;
  const SHARE_STEP     = isClubMode ? 4 : 3;
  const stepLabels = isClubMode ? tr.stepsClub : tr.steps;
  const totalSteps = stepLabels.length;

  // Phase 2 — when the coach picks an event from the inline picker,
  // we remember which one (so the picker stays highlighted across step
  // navigation) and what we auto-derived (so we can mark each field with
  // a "✨ Auto-filled" badge until the coach edits it).
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [autoFilled, setAutoFilled] = useState({});

  // Send-to-roster state (Phase 1 of comms integration). These are only
  // relevant on Step 4 but live up here so the preview call can fire the
  // moment we enter Step 4 (not on every keystroke during Review).
  const [audience, setAudience]             = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceError, setAudienceError]   = useState(null);
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [sentReceipt, setSentReceipt]       = useState(null);

  // Per-language audience split for the Routing Summary block. Now
  // sourced from the single previewTeamBroadcast response (its
  // `languageBreakdown` field) — kept as a separate piece of state so
  // RoutingSummary keeps its existing { en, es } shape contract.
  // Skipped when the user has explicitly filtered to one language (the
  // main preview already tells us the count for that bucket).
  const [langSplit, setLangSplit]               = useState(null);
  const [langSplitLoading, setLangSplitLoading] = useState(false);

  // Phase 2: audience filters. Defaults to "everyone" so existing coaches
  // get the same behavior as Phase 1 unless they deliberately open the
  // filter panel. `seasonId = null` means "all seasons" — we don't auto-
  // pick the open season because a coach whose season just ended still
  // wants to message their old roster.
  const [filters, setFilters] = useState({
    seasonId: null,
    paymentStatus: 'all',   // 'all' | 'paid' | 'unpaid'
    language: 'all',        // 'all' | 'en'  | 'es'
    audienceMode: 'all',    // 'all' | 'missing_docs'
    // Club-mode-only: scope to one program's registrants. Server's
    // applyAudienceFilters narrows the audience to regs whose programId
    // matches. Set via ?programId= or ?audience=program-registrants.
    programId: null,
  });

  // Single-recipient lock — set when the URL has ?prefilledFor={regId}
  // (e.g. coach taps "Send reminder" on one row of the financials table).
  // While set, the audience reduces to that one registration; coach can
  // unlock by clicking the "send to whole team" link in the banner.
  const [prefilledRegistration, setPrefilledRegistration] = useState(null); // { id, playerName, parentName, ... } | null
  const [seasons, setSeasons]             = useState([]);
  const [seasonsLoading, setSeasonsLoading] = useState(false);

  // Pre-select type from URL param ?type=Reminder etc.
  // If the param is set but doesn't match any known type, surface
  // a toast so the user knows the deep link was busted (smart-action
  // tiles on the Dashboard generate these URLs; a typo there used to
  // silently no-op and dump the user on the type picker with no
  // explanation).
  //
  // Also consumes the audience-side URL params from FinancialReports +
  // RegistrationsDashboard:
  //   ?seasonId=…             → pre-pick that season in the filter
  //   ?audience=unpaid        → pre-set paymentStatus = 'unpaid'
  //   ?audience=missing_docs  → pre-set audienceMode  = 'missing_docs'
  //   ?audience=program-registrants&programId=X
  //                           → club mode only; narrows the audience to
  //                             registrations with programId=X
  //   ?prefilledFor={regId}   → lock the audience to that single registration
  //
  // Club-mode-only params:
  //   ?teams=t1,t2,t3         → pre-select these teams in the picker
  //   ?programId=X            → derive teams from program.divisions[].teamId
  //                             when ?teams= is omitted (Programs bulk-send)
  //
  // All of these are smart defaults; the coach can still adjust the
  // audience filter on Step 4 to broaden again. When type= is set the
  // wizard advances PAST the type-picker step (substantive PICK_TYPE+1)
  // — in club mode that's still the team picker if the director needs
  // to confirm teams, except when ?teams= is also provided in which
  // case we fast-forward to Details.
  useEffect(() => {
    const typeParam = searchParams.get('type');
    let advanceTo = null;
    if (typeParam) {
      const found = MESSAGE_TYPES.find(
        (t) => t.id.toLowerCase() === typeParam.toLowerCase()
      );
      if (found) {
        const saved = loadLastDetails(found.id);
        setSelectedType(found);
        setLastDetails(saved);
        setDetails(saved || {});
        // In single-team mode skip the picker (step 1 = details). In
        // club mode the user still needs to confirm teams unless a
        // ?teams= or ?programId= param is supplied — handled below.
        advanceTo = isClubMode
          ? (searchParams.get('teams') || searchParams.get('programId') ? DETAILS_STEP : PICK_TYPE_STEP)
          : DETAILS_STEP; // = 1 in single-team mode
      } else {
        // Invalid type param. Show what was attempted + the valid set.
        const validTypes = MESSAGE_TYPES.map((t) => t.id).join(', ');
        toast.error(
          `Unknown message type "${typeParam}". Pick one below. ` +
          `(Valid: ${validTypes})`,
          { duration: 6000 }
        );
      }
    }
    if (advanceTo != null) setStep(advanceTo);

    // Audience-side defaults — applied independently of type, so deep
    // links from the dashboard work even when the coach lands on Step 0.
    const seasonIdParam   = searchParams.get('seasonId');
    const audienceParam   = String(searchParams.get('audience') || '').toLowerCase();
    const programIdParam  = searchParams.get('programId');
    const filterPatch = {};
    if (seasonIdParam) filterPatch.seasonId = seasonIdParam;
    if (audienceParam === 'unpaid')        filterPatch.paymentStatus = 'unpaid';
    if (audienceParam === 'missing_docs')  filterPatch.audienceMode  = 'missing_docs';
    // Programs bulk-send: scope the audience to one program (server's
    // applyAudienceFilters takes care of the actual narrowing).
    if (audienceParam === 'program-registrants' && programIdParam) {
      filterPatch.programId = programIdParam;
    } else if (programIdParam && isClubMode) {
      // Plain ?programId= (no audience= flag) still scopes to the program
      // — we don't have a use case for "include program parents and
      // others" in the same broadcast.
      filterPatch.programId = programIdParam;
    }
    if (Object.keys(filterPatch).length > 0) {
      setFilters((prev) => ({ ...prev, ...filterPatch }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Club-mode: load each team referenced by club.teamIds ────────────────
  // useClub gives us the list of teamIds; we fan out to grab each team's
  // human-readable name + sport/age/gender so the picker is useful (the
  // ids alone aren't recognizable to a director with 12 teams).
  useEffect(() => {
    if (!isClubMode) return;
    if (!club?.teamIds?.length) {
      setClubTeams([]);
      setClubTeamsLoading(false);
      return;
    }
    const ids = club.teamIds.filter(Boolean);
    setClubTeamsLoading(true);
    Promise.all(ids.map((id) => getDoc(doc(db, 'teams', id))))
      .then((snaps) => {
        const list = snaps
          .filter((s) => s.exists())
          .map((s) => ({ id: s.id, ...s.data() }));
        setClubTeams(list);
        // Default selection: every team in the club. If a ?teams= or
        // ?programId= URL param is present, the next effect will narrow
        // the selection — we set the all-teams default here so directors
        // arriving without a pre-scoped link still see "everyone selected".
        setSelectedTeamIds((prev) => (prev.length === 0 ? list.map((t) => t.id) : prev));
      })
      .catch((err) => console.error('[ComposerWizard] load club teams failed', err))
      .finally(() => setClubTeamsLoading(false));
  }, [isClubMode, club?.teamIds?.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Club-mode: pre-select teams from URL ?teams=t1,t2,t3 ─────────────────
  // Applied once teams are loaded. Filters out any ids that aren't actually
  // in this club (defense against stale or forged URLs). The
  // didApplyTeamsParam flag prevents re-running on club-team list churn.
  useEffect(() => {
    if (!isClubMode || didApplyTeamsParam) return;
    if (clubTeams.length === 0) return;
    const teamsParam = searchParams.get('teams');
    if (teamsParam) {
      const requested = teamsParam.split(',').map((s) => s.trim()).filter(Boolean);
      const valid = new Set(clubTeams.map((t) => t.id));
      const filtered = requested.filter((id) => valid.has(id));
      if (filtered.length > 0) {
        setSelectedTeamIds(filtered);
      }
      setDidApplyTeamsParam(true);
      return;
    }
    // ?programId= — derive teams from program.divisions[].teamId. Same
    // safety filter against the club's teamId set so a stale program
    // pointing to detached teams collapses to "all teams in club".
    const programIdParam = searchParams.get('programId');
    if (programIdParam) {
      // Lazy program load — only when we actually need teams from it.
      (async () => {
        try {
          const snap = await getDoc(doc(db, 'programs', programIdParam));
          if (!snap.exists()) {
            setDidApplyTeamsParam(true);
            return;
          }
          const prog = { id: snap.id, ...snap.data() };
          setProgram(prog);
          const divisionTeamIds = (prog.divisions || [])
            .map((d) => d?.teamId)
            .filter(Boolean);
          const valid = new Set(clubTeams.map((t) => t.id));
          const filtered = Array.from(new Set(divisionTeamIds.filter((id) => valid.has(id))));
          if (filtered.length > 0) {
            setSelectedTeamIds(filtered);
          }
        } catch (err) {
          console.warn('[ComposerWizard] program load failed', err);
        } finally {
          setDidApplyTeamsParam(true);
        }
      })();
    } else {
      setDidApplyTeamsParam(true);
    }
  }, [isClubMode, clubTeams, didApplyTeamsParam, searchParams]);

  const handleToggleTeam = useCallback((id) => {
    setSelectedTeamIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);
  const handleToggleAllTeams = useCallback(() => {
    setSelectedTeamIds((prev) => (prev.length === clubTeams.length ? [] : clubTeams.map((t) => t.id)));
  }, [clubTeams]);

  // The teamIds array we hand to backend callables. In single-team mode
  // it's just [teamId]; in club mode it's the user's multi-select.
  const effectiveTeamIds = isClubMode ? selectedTeamIds : (teamId ? [teamId] : []);

  const handleDetailChange = useCallback((name, value) => {
    setDetails((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectType = (type) => {
    const saved = loadLastDetails(type.id);
    setSelectedType(type);
    setLastDetails(saved);
    setDetails(saved || {});
    // New type → drop any prefill state from the previous type so the
    // picker doesn't keep showing a stale "selected" highlight on a
    // candidate that was for a different message type.
    setSelectedEventId(null);
    setAutoFilled({});
  };

  // Coach picked an event from the inline picker — derive the per-type
  // detail fields and merge them into the form. We DON'T clobber fields
  // the coach has already typed into (a value present in details that
  // wasn't part of the previous auto-fill), so flipping between two
  // events doesn't wipe out their highlights/whatToBring text.
  //
  // "Auto-fill" has two sources we need to treat as overwriteable on
  // event-pick:
  //   1. `autoFilled` — values from a previous event pick this session
  //   2. `lastDetails` — values loaded from localStorage when the
  //      coach selected this message type (the "Pre-filled from your
  //      last Reminder" banner). Without this second check the
  //      previously-loaded last-draft values look like coach-typed
  //      values and the event pick silently merges with them instead
  //      of overriding (see status snapshot 2026-05-17 bug list).
  const handlePickEvent = useCallback((eventId, candidate) => {
    if (!candidate || !selectedType) return;
    const derived = deriveDetailsForType(selectedType.id, candidate, {});
    setSelectedEventId(eventId);
    setAutoFilled(derived);
    setDetails((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(derived)) {
        const prevVal = prev[k];
        const wasAutoFilled = autoFilled[k] != null
          && String(autoFilled[k]).length > 0
          && String(prevVal) === String(autoFilled[k]);
        const wasFromLastDetails = lastDetails && lastDetails[k] != null
          && String(lastDetails[k]).length > 0
          && String(prevVal) === String(lastDetails[k]);
        if (!prevVal || wasAutoFilled || wasFromLastDetails) {
          next[k] = v;
        }
      }
      return next;
    });
  }, [selectedType, autoFilled, lastDetails]);

  const handleSkipPick = useCallback(() => {
    setSelectedEventId(null);
    // Note: we don't strip already-filled values — the coach may have
    // tweaked some after a previous pick. They can manually clear via
    // the "Clear all" link in the existing prefill banner.
  }, []);

  const canAdvance = () => {
    // Club mode adds a Teams step at index 0 — must have at least one
    // team selected to advance to type picker.
    if (step === TEAMS_STEP) return selectedTeamIds.length > 0;
    if (step === PICK_TYPE_STEP) return !!selectedType;
    if (step === DETAILS_STEP) {
      const required = selectedType?.fields?.filter((f) => f.required) || [];
      return required.every((f) => (details[f.name] || '').toString().trim().length > 0);
    }
    return true;
  };

  const handleNext = async () => {
    if (step === DETAILS_STEP) {
      setGenerating(true);
      try {
        const functions = getFunctions();
        const generateMessage = httpsCallable(functions, 'generateMessage');

        const detailsWithCoach = {
          ...details,
          coachName: details.coachName || userProfile?.firstName || userProfile?.displayName || '',
        };

        // Save to localStorage for next time
        saveLastDetails(selectedType.id, detailsWithCoach);

        // Single-team mode → teamId; club mode → teamIds array (the
        // backend Spanish-detection now iterates the full set so a club
        // broadcast generates Spanish whenever ANY selected team has
        // Spanish-preference parents — not just the first one).
        const result = await generateMessage({
          teamId: effectiveTeamIds[0] || teamId,
          ...(isClubMode ? { teamIds: effectiveTeamIds } : {}),
          messageType: selectedType.id,
          details: detailsWithCoach,
          // Always generate both versions in the manual composer —
          // the coach is reviewing/editing both before sharing, and
          // marketing promises bilingual is built in. The backend's
          // hasSpanishParents cost optimization still applies on the
          // broadcast pipeline (where empty Spanish for English-only
          // clubs is fine).
          forceBilingual: true,
        });

        setMessageId(result.data.messageId);
        setBodyEn(result.data.bodyEn);
        setBodyEs(result.data.bodyEs);

        // Club mode: write a draft clubBroadcasts/{} doc so the eventual
        // sendClubBroadcastSms call can update it in place (matches the
        // pattern from the now-replaced ClubBroadcast page). Non-fatal:
        // a failed draft write doesn't block the wizard, the actual send
        // function will create one.
        if (isClubMode && clubId) {
          try {
            const draftRef = await addDoc(collection(db, 'clubBroadcasts'), {
              clubId,
              clubName:    club?.name || null,
              teamIds:     effectiveTeamIds,
              messageType: selectedType.id,
              bodyEn:      result.data.bodyEn,
              bodyEs:      result.data.bodyEs || null,
              createdBy:   firebaseUser?.uid || null,
              createdAt:   serverTimestamp(),
              status:      'draft',
            });
            setClubBroadcastId(draftRef.id);
          } catch (err) {
            console.warn('[ComposerWizard] club draft save failed', err);
          }
        }

        setStep(REVIEW_STEP);
      } catch (err) {
        console.error('[ComposerWizard] generateMessage error', err);
        toast.error(err.message || tr.genErrToast);
      } finally {
        setGenerating(false);
      }
      return;
    }

    setStep((s) => Math.min(s + 1, totalSteps - 1));
  };

  const handleMarkSent = async () => {
    setSending(true);
    try {
      if (messageId) {
        // Stamp relatedEventId from the URL param if present (e.g.
        // routed in via `?type=Game%20Recap&relatedEventId={gameId}`).
        // TeamSmartActions' "recap not sent" prompt queries
        // teams/{}/messages where relatedEventId == game.id to suppress
        // its own surface once the coach has shared a recap. Without
        // this stamp, the recap prompt would re-fire forever.
        const relatedEventId = searchParams.get('relatedEventId') || null;
        await updateDoc(
          doc(db, 'teams', teamId, 'messages', messageId),
          {
            bodyEn,
            bodyEs,
            status: 'sent',
            sentAt: serverTimestamp(),
            ...(relatedEventId ? { relatedEventId } : {}),
          }
        );
      }
      toast.success(tr.savedToast);
      navigate(`/teams/${teamId}/messages`);
    } catch (err) {
      console.error(err);
      toast.error(tr.saveErrToast);
    } finally {
      setSending(false);
    }
  };

  // --- Load seasons for the filter dropdown ----------------------------
  // Single read (not a subscription) — seasons don't churn during a send
  // flow, and we'd rather pay one read per composer session than keep a
  // listener open. Fires lazily on Step 4 entry so we don't cost an extra
  // read for coaches who never open the filter panel on short sessions.
  // In club mode the season filter is hidden (per-team season picker
  // doesn't generalize across clubs) so we skip the load entirely.
  useEffect(() => {
    if (step !== SHARE_STEP || !teamId || isClubMode) return;
    if (seasons.length > 0) return; // already loaded this session
    let cancelled = false;
    (async () => {
      setSeasonsLoading(true);
      try {
        const q = query(
          collection(db, 'seasons'),
          where('teamId', '==', teamId),
          orderBy('createdAt', 'desc'),
        );
        const snap = await getDocs(q);
        if (!cancelled) {
          setSeasons(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error('[ComposerWizard] load seasons failed', err);
        // Non-fatal — the filter panel falls back to "All seasons" with
        // no dropdown options. Coach can still send to the full roster.
      } finally {
        if (!cancelled) setSeasonsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, teamId, isClubMode, seasons.length, SHARE_STEP]);

  // --- Prefilled-registration lookup (for ?prefilledFor=…) -------------
  // One-shot read of the single registration doc so the lock banner can
  // show "Sending to: {playerName} ({parentName})". Lazy: only fires when
  // the param is set and the lock hasn't been cleared. If the read fails
  // (deleted reg, permission, etc.) we silently drop the lock so the
  // composer falls back to the normal whole-team audience.
  useEffect(() => {
    const regId = searchParams.get('prefilledFor');
    if (!regId) return;
    if (prefilledRegistration && prefilledRegistration.id === regId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'registrations', regId));
        if (cancelled) return;
        if (!snap.exists()) {
          console.warn('[ComposerWizard] prefilledFor registration not found', regId);
          return;
        }
        setPrefilledRegistration({ id: snap.id, ...snap.data() });
      } catch (err) {
        console.warn('[ComposerWizard] prefilledFor load failed', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Coach can break the prefilledFor lock to re-target the whole team.
  // Clear both the in-memory record and the URL param so a back/forward
  // doesn't silently re-arm it. Filters keep their other URL-seeded
  // defaults (audience=unpaid, seasonId) — only the single-recipient
  // pin is removed.
  const handleUnlockPrefilled = useCallback(() => {
    setPrefilledRegistration(null);
    // Drop the param from the URL without a full navigation so the
    // composer's other state (step, filters, generated bodies) survives.
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('prefilledFor');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // --- Send-to-roster: preview audience when user lands on Step 4 ------
  // Also re-fires when filters change. Debounced 250ms so rapid chip
  // taps (paymentStatus → language) collapse into one callable invocation
  // instead of 3 — cuts function invocations on the happy path by ~2/3.
  //
  // One-shot call, not a live subscription — rosters don't change between
  // "Review" and "Share" in the same session, and a subscription here
  // would cost a Firestore read on every registration write across the
  // entire team while the composer is open.
  useEffect(() => {
    if (step !== SHARE_STEP) return;
    if (effectiveTeamIds.length === 0) return;
    let cancelled = false;

    // ── Prefilled-single-recipient short-circuit ────────────────────────
    // When ?prefilledFor locks the audience to one registration, we don't
    // need a server preview — we already have the doc client-side from the
    // prefilled-load effect. Synthesize the same response shape (channel
    // counts, language breakdown, rate-limit) so RoutingSummary +
    // AudienceSummary keep working unchanged.
    if (prefilledRegistration) {
      const reg = prefilledRegistration;
      const phoneOk = /^\+\d{10,15}$/.test(String(reg?.parentPhone || ''));
      const isSms   = reg?.smsOptIn === true && phoneOk;
      const hasEmail = !!String(reg?.parentEmail || '').trim();
      const channel = isSms ? 'sms' : (hasEmail ? 'email' : 'none');

      const langKey = (() => {
        // Canonical `language` field wins over legacy `parentLanguage`
        // (matches teamBroadcast.js bucketLanguage + the resolver chain
        // in functions/utils/parentLanguage.js).
        const raw = String(reg?.language || reg?.parentLanguage || '').toLowerCase();
        if (!raw) return 'en';
        if (raw.startsWith('es')) return 'es';
        if (raw.startsWith('en')) return 'en';
        return 'unknown';
      })();

      const empty = { sms: 0, email: 0, total: 0 };
      const breakdown = { en: { ...empty }, es: { ...empty }, unknown: { ...empty } };
      if (channel === 'sms') {
        breakdown[langKey].sms   = 1;
        breakdown[langKey].total = 1;
      } else if (channel === 'email') {
        breakdown[langKey].email = 1;
        breakdown[langKey].total = 1;
      }

      setAudience({
        smsCount:           channel === 'sms'   ? 1 : 0,
        emailCount:         channel === 'email' ? 1 : 0,
        unreachableCount:   channel === 'none'  ? 1 : 0,
        totalRegistrations: 1,
        rosterTotal:        1,
        rateLimitRemaining: 5,           // optimistic — server enforces on send
        languageBreakdown:  breakdown,
      });
      setAudienceLoading(false);
      setAudienceError(null);
      return;
    }

    // Debounce filter thrash. The initial mount fires immediately
    // (setTimeout 0) so there's no perceptible delay on entering Step 4.
    const isInitial = audience === null && !audienceError;
    const debounceMs = isInitial ? 0 : 250;

    const t = setTimeout(async () => {
      setAudienceLoading(true);
      setAudienceError(null);
      try {
        const functions = getFunctions();
        const previewTeamBroadcast = httpsCallable(functions, 'previewTeamBroadcast');
        // Both single-team and club-mode now go through the same callable;
        // it accepts teamIds[] and dedupes registrations across teams.
        const res = await previewTeamBroadcast({
          teamId: effectiveTeamIds[0] || teamId,
          ...(isClubMode ? { teamIds: effectiveTeamIds } : {}),
          seasonId: filters.seasonId || null,
          filters: {
            paymentStatus: filters.paymentStatus,
            language:      filters.language,
            audienceMode:  filters.audienceMode,
            // programId is club-mode only — applyAudienceFilters narrows
            // to families whose reg.programId matches.
            ...(filters.programId ? { programId: filters.programId } : {}),
          },
        });
        if (!cancelled) setAudience(res.data);
      } catch (err) {
        console.error('[ComposerWizard] previewTeamBroadcast failed', err);
        // Don't crash Step 4 if the preview fails — coaches can still use
        // the copy/paste fallback. Just surface the error and skip the
        // audience pill.
        if (!cancelled) setAudienceError(err?.message || 'preview_failed');
      } finally {
        if (!cancelled) setAudienceLoading(false);
      }
    }, debounceMs);

    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step, teamId, isClubMode, effectiveTeamIds.join(','),
    filters.seasonId, filters.paymentStatus, filters.language, filters.audienceMode,
    filters.programId,
    prefilledRegistration?.id,
  ]);

  // --- Per-language audience split for Routing Summary -----------------
  // Derived from the single previewTeamBroadcast response (`languageBreakdown`
  // field) so we no longer fire two extra callable calls per Step 4 entry.
  // When the coach has narrowed the language filter, we still null this out
  // — RoutingSummary uses the main totals in that case.
  useEffect(() => {
    if (step !== SHARE_STEP || !audience) {
      setLangSplit(null);
      setLangSplitLoading(false);
      return;
    }
    if (filters.language !== 'all') {
      setLangSplit(null);
      setLangSplitLoading(false);
      return;
    }
    const breakdown = audience.languageBreakdown;
    if (!breakdown) {
      // Older preview response (cache miss after deploy, etc.) — degrade
      // gracefully to no split rather than fabricating zeros.
      setLangSplit(null);
      setLangSplitLoading(false);
      return;
    }
    // Match the previous langSplit shape so RoutingSummary doesn't change.
    // 'unknown' is collapsed into 'en' for display since pickSmsBody falls
    // back to English for those parents (so they read in English anyway).
    const en = breakdown.en      || { sms: 0, email: 0, total: 0 };
    const es = breakdown.es      || { sms: 0, email: 0, total: 0 };
    const uk = breakdown.unknown || { sms: 0, email: 0, total: 0 };
    setLangSplit({
      en: {
        smsCount:   (en.sms   || 0) + (uk.sms   || 0),
        emailCount: (en.email || 0) + (uk.email || 0),
        total:      (en.total || 0) + (uk.total || 0),
      },
      es: {
        smsCount:   es.sms   || 0,
        emailCount: es.email || 0,
        total:      es.total || 0,
      },
    });
    setLangSplitLoading(false);
  }, [step, audience, filters.language]);

  // --- Filter mutators --------------------------------------------------
  // Merge-patch helper — the chip groups only know their own slice of
  // state, so we accept partials and spread into the current object.
  const handleFiltersChange = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilters({
      seasonId: null,
      paymentStatus: 'all',
      language: 'all',
      audienceMode: 'all',
    });
  }, []);

  // Coach tapped the primary "Send to roster" button: open the confirm.
  // We deliberately don't fire the send yet — the dialog shows the exact
  // audience count again so there's a double-check before 89 phones buzz.
  const handleRequestSend = () => setConfirmOpen(true);

  const handleConfirmSend = async (attested = false) => {
    if (sending) return;
    setSending(true);
    try {
      const functions = getFunctions();

      const coachNameForEmail =
        details.coachName ||
        userProfile?.firstName ||
        userProfile?.displayName ||
        null;

      // Backend fork: club mode uses sendClubBroadcastSms (multi-team
      // fan-out, club-level rate limit, club + per-team audit docs);
      // single-team mode keeps using sendTeamBroadcast (per-team rate
      // limit, hybrid SMS+email channel router). Both return enough
      // metadata to build the receipt below.
      let smsSent = 0;
      let emailSent = 0;
      let smsFailed = 0;
      let emailFailed = 0;
      let smsHeld = 0;   // quiet-hours holds — team path only, club path has no SMS quiet gate here
      let status = null;
      if (isClubMode) {
        const sendClubBroadcastSms = httpsCallable(functions, 'sendClubBroadcastSms');
        const res = await sendClubBroadcastSms({
          clubId,
          teamIds:         effectiveTeamIds,
          bodyEn,
          bodyEs:          bodyEs || null,
          messageType:     selectedType?.id || null,
          clubBroadcastId: clubBroadcastId || null,
        });
        const data = res?.data || {};
        // sendClubBroadcastSms returns { sent, failed, sentPush, sentSms }
        // — collapse push + SMS into "smsSent" for the receipt since the
        // existing UI doesn't distinguish (both are non-email channels).
        // status is one of 'sent' | 'partial' | 'failed' | 'no_recipients'.
        const sent = data.sent || 0;
        const failed = data.failed || 0;
        smsSent = sent;            // email count is always 0 for the club path today
        emailSent = 0;
        smsFailed = failed;
        emailFailed = 0;
        status = data.status || null;
      } else {
        const sendTeamBroadcast = httpsCallable(functions, 'sendTeamBroadcast');
        const res = await sendTeamBroadcast({
          teamId,
          bodyEn,
          bodyEs,
          messageType: selectedType?.id || null,
          messageId: messageId || null,
          coachName: coachNameForEmail,
          // F9: one-time "I have permission" attestation for first-time roster
          // contacts. The server enforces it (and persists it) — this is the
          // checkbox the coach ticked in the confirm modal.
          attestation: attested === true,
          // Pass the current filter set — server re-applies, so client-side
          // tampering with the preview state can't broaden the audience.
          seasonId: filters.seasonId || null,
          filters: {
            paymentStatus: filters.paymentStatus,
            language:      filters.language,
            audienceMode:  filters.audienceMode,
            // Single-recipient lock from ?prefilledFor — server intersects
            // with the team's roster, so a forged id can't reach a parent
            // outside this team.
            ...(prefilledRegistration
              ? { registrationIds: [prefilledRegistration.id] }
              : {}),
          },
        });
        const data = res.data || {};
        smsSent     = data.smsSent     || 0;
        emailSent   = data.emailSent   || 0;
        smsFailed   = data.smsFailed   || 0;
        emailFailed = data.emailFailed || 0;
        smsHeld     = data.smsHeld     || 0;
        status      = data.status      || null;
      }

      // Build the receipt string from the literal counts. The partial
      // version shows totals because surfacing "3 SMS failed · 2 email
      // sent" at-a-glance is harder to scan than a combined success/fail.
      const totalSent   = smsSent + emailSent;
      const totalFailed = smsFailed + emailFailed;
      const receipt = totalFailed > 0
        ? tr.sendReceiptPartial
            .replace('{sent}',   totalSent)
            .replace('{failed}', totalFailed)
        : tr.sendReceipt
            .replace('{sms}',   smsSent)
            .replace('{email}', emailSent);
      // Quiet-hours holds get their own sentence — they are not failures,
      // and the coach's next move (resend after 7 AM) is different.
      const heldNote = smsHeld > 0
        ? ' ' + tr.sendReceiptHeld.replace('{held}', smsHeld)
        : '';

      setSentReceipt(receipt + heldNote);
      setConfirmOpen(false);

      // Mirror the status onto the teams/{}/messages/{} doc so this shows
      // up in the message history. The Cloud Function also does this
      // server-side — we update here too so the navigate() lands on a
      // history list that already reflects the send (no refresh needed).
      // relatedEventId stamp lets TeamSmartActions' recap prompt suppress
      // itself once the coach has shared a recap (see handleMarkSent).
      // Only meaningful in single-team mode — club broadcasts write per-
      // team broadcast audit docs server-side instead, and there's no
      // `messages/{}` doc to flip on the club path.
      if (!isClubMode && messageId && teamId) {
        const relatedEventId = searchParams.get('relatedEventId') || null;
        updateDoc(
          doc(db, 'teams', teamId, 'messages', messageId),
          {
            bodyEn,
            bodyEs,
            status: 'sent',
            sentAt: serverTimestamp(),
            ...(relatedEventId ? { relatedEventId } : {}),
          }
        ).catch(() => { /* non-critical — server already wrote this */ });
      }

      toast.success(receipt);

      // Let the coach see the receipt for a beat, then bounce to history.
      // Club mode lands back on the club dashboard since per-team message
      // history makes less sense as a single destination.
      setTimeout(() => {
        navigate(isClubMode ? `/clubs/${clubId}` : `/teams/${teamId}/messages`);
      }, 1800);
    } catch (err) {
      console.error('[ComposerWizard] send failed', err);
      // Firebase callable errors land as { code, message } — prefer the
      // human message (e.g. the 5/24h rate-limit explanation) over the
      // generic fallback.
      toast.error(err?.message || tr.sendErr);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7faf5] p-4 sm:p-6 lg:p-8">
      {confirmOpen && (
        <ConfirmSendModal
          audience={audience}
          onConfirm={handleConfirmSend}
          onCancel={() => !sending && setConfirmOpen(false)}
          sending={sending}
          tr={tr}
        />
      )}
      <div className="max-w-2xl mx-auto">
        {/* Team sub-nav: only meaningful in single-team mode. Club-mode
            navigation is handled by the back button and the Club's own
            sidebar layout. */}
        {!isClubMode && <TeamSubNav teamId={teamId} />}

        <button
          onClick={() => {
            if (step > 0 && !generating) {
              setStep((s) => s - 1);
            } else {
              navigate(isClubMode ? `/clubs/${clubId}` : `/teams/${teamId}/messages`);
            }
          }}
          className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {step === 0 ? tr.backToMessages : tr.back}
        </button>

        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-zinc-800 shadow-sm p-6 sm:p-8">
          <StepBar current={step} labels={stepLabels} />

          <div className="min-h-[300px]">
            {step === TEAMS_STEP && (
              <StepPickTeams
                teams={clubTeams}
                teamsLoading={clubTeamsLoading || clubLoading}
                selectedTeamIds={selectedTeamIds}
                onToggleTeam={handleToggleTeam}
                onToggleAll={handleToggleAllTeams}
                programName={program?.name || null}
                tr={tr}
              />
            )}
            {step === PICK_TYPE_STEP && (
              <StepPickType selected={selectedType} onSelect={handleSelectType} tr={tr} lang={lang} />
            )}
            {step === DETAILS_STEP && selectedType && (
              <StepDetails
                type={selectedType}
                details={details}
                onChange={handleDetailChange}
                lastDetails={lastDetails}
                tr={tr}
                lang={lang}
                teamIds={effectiveTeamIds}
                selectedEventId={selectedEventId}
                onPickEvent={handlePickEvent}
                onSkipPick={handleSkipPick}
                autoFilled={autoFilled}
              />
            )}
            {step === REVIEW_STEP && (
              <StepReview
                type={selectedType}
                bodyEn={bodyEn}
                bodyEs={bodyEs}
                onEditEn={setBodyEn}
                onEditEs={setBodyEs}
                tr={tr}
                // The same field generateMessage reads server-side. Keeps
                // the chip in sync with what the AI actually used.
                messageTone={userProfile?.messageTone}
                onChangeVoice={() => navigate('/settings/profile')}
                lang={lang}
                teamCount={effectiveTeamIds.length}
              />
            )}
            {step === SHARE_STEP && (
              <StepShare
                bodyEn={bodyEn}
                bodyEs={bodyEs}
                tr={tr}
                audience={audience}
                audienceLoading={audienceLoading}
                audienceError={audienceError}
                langSplit={langSplit}
                langSplitLoading={langSplitLoading}
                onRequestSend={handleRequestSend}
                onMarkSent={handleMarkSent}
                sending={sending}
                sentReceipt={sentReceipt}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onFiltersReset={handleFiltersReset}
                seasons={seasons}
                seasonsLoading={seasonsLoading}
                prefilledRegistration={prefilledRegistration}
                onUnlockPrefilled={handleUnlockPrefilled}
                // New tab, not navigate(): the wizard's generated drafts live in
                // component state, so an in-app route change here throws away
                // the coach's message right when they went to invite someone.
                onInviteFamilies={isClubMode ? null : () => window.open(`/teams/${teamId}/roster`, '_blank', 'noopener')}
                lang={lang}
              />
            )}
          </div>

          {/* Nav buttons — hide on the Share step (it has its own send CTA).
              Generate button label fires from the Details step (which is the
              one immediately before Review). */}
          {step !== SHARE_STEP && (
            <div className="mt-8 pt-6 border-t border-zinc-100 flex justify-end">
              <button
                type="button"
                onClick={handleNext}
                disabled={!canAdvance() || generating}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-6 py-3 text-sm font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' }}
              >
                {generating ? (
                  <><ArrowPathIcon className="h-4 w-4 animate-spin" />{tr.generating}</>
                ) : step === DETAILS_STEP ? (
                  <><ArrowPathIcon className="h-4 w-4" />{tr.generateBtn}</>
                ) : (
                  <>{tr.next}<ChevronRightIcon className="h-4 w-4" /></>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
